import { formatDateTime } from '@pickleballinc/lib/format-dates';

import { DateFormat, GameScore } from '@/modules/players/v2/tabs/overview/TournamentResults/types';
import { PlayerLiveGameStatusData } from '@/types/players.types';

export const getBracketStatus = (GoLiveStatusID?: number) => {
  switch (GoLiveStatusID) {
    case 2:
    case 5:
    case 6:
      return 'In Progress';
    case 3:
      return 'Bracket Completed';
    case 4:
      return 'Paused';
    default:
      return 'Not Started Yet';
  }
};

export const formatDate = (dates: {
  DateOfActivity?: string;
  DateStart?: string;
  DateFinish?: string;
}): DateFormat => {
  let dateString: string;

  if (dates.DateFinish && dates.DateFinish !== 'null') {
    dateString = dates.DateFinish;
  } else if (dates.DateStart && dates.DateStart !== 'null') {
    dateString = dates.DateStart;
  } else if (dates.DateOfActivity && dates.DateOfActivity !== 'null') {
    dateString = dates.DateOfActivity;
  } else {
    return {
      date: '',
    };
  }

  return {
    date: `${formatDateTime(dateString, { parseZone: true, includeYear: false })}`,
  };
};

export const getTeams = (match: PlayerLiveGameStatusData) => {
  const {
    T1_P1_UserID,
    T1_P2_UserID,
    T2_P1_UserID,
    T2_P2_UserID,
    T1_P1_FirstName,
    T1_P1_LastName,
    T1_P1_NameSuffix,
    T1_P2_FirstName,
    T1_P2_LastName,
    T1_P2_NameSuffix,
    T1_P2_Gender,
    T2_P1_FirstName,
    T2_P1_LastName,
    T2_P1_NameSuffix,
    T2_P2_FirstName,
    T2_P2_LastName,
    T2_P2_NameSuffix,
    T2_P2_Gender,
  } = match;

  const isTeamOneTBD = T1_P1_FirstName === 'TBD' && (!T1_P2_UserID || T1_P2_FirstName === 'TBD');
  const isTeamTwoTBD = T2_P1_FirstName === 'TBD' && (!T2_P2_UserID || T2_P2_FirstName === 'TBD');

  if (isTeamOneTBD) {
    return { teamOne: 'TBD', teamTwo: isTeamTwoTBD ? 'TBD' : formatTeamTwo() };
  } else if (isTeamTwoTBD) {
    return { teamOne: formatTeamOne(), teamTwo: 'TBD' };
  }

  function formatPlayerName(firstName: string, lastName: string, suffix?: string) {
    const formattedName = `${firstName}, ${lastName}${suffix ? ' ' + suffix : ''}`;
    return formattedName.trim();
  }

  function formatTeamOne() {
    let teamOne = '';
    if (T1_P2_UserID && T1_P2_UserID !== 'null' && T1_P2_Gender === 'F') {
      teamOne = formatPlayerName(T1_P2_FirstName, T1_P2_LastName, T1_P2_NameSuffix);
      if (T1_P1_UserID && T1_P1_UserID !== 'null') {
        teamOne += ', ' + formatPlayerName(T1_P1_FirstName, T1_P1_LastName, T1_P1_NameSuffix);
      }
    } else if (T1_P1_UserID && T1_P1_UserID !== 'null') {
      teamOne = formatPlayerName(T1_P1_FirstName, T1_P1_LastName, T1_P1_NameSuffix);
      if (T1_P2_UserID && T1_P2_UserID !== 'null') {
        teamOne += ', ' + formatPlayerName(T1_P2_FirstName, T1_P2_LastName, T1_P2_NameSuffix);
      }
    }
    return teamOne;
  }

  function formatTeamTwo() {
    let teamTwo = '';
    if (T2_P2_UserID && T2_P2_UserID !== 'null' && T2_P2_Gender === 'F') {
      teamTwo = formatPlayerName(T2_P2_FirstName, T2_P2_LastName, T2_P2_NameSuffix);
      if (T2_P1_UserID && T2_P1_UserID !== 'null') {
        teamTwo += ', ' + formatPlayerName(T2_P1_FirstName, T2_P1_LastName, T2_P1_NameSuffix);
      }
    } else if (T2_P1_UserID && T2_P1_UserID !== 'null') {
      teamTwo = formatPlayerName(T2_P1_FirstName, T2_P1_LastName, T2_P1_NameSuffix);
      if (T2_P2_UserID && T2_P2_UserID !== 'null') {
        teamTwo += ', ' + formatPlayerName(T2_P2_FirstName, T2_P2_LastName, T2_P2_NameSuffix);
      }
    }
    return teamTwo;
  }

  return { teamOne: formatTeamOne(), teamTwo: formatTeamTwo() };
};

const getForfeitMessage = (team1Forfeit: number, team2Forfeit: number): string | undefined => {
  if (team1Forfeit === 1 || team2Forfeit === 1) return 'FF';
  if (team1Forfeit === 2 || team2Forfeit === 2) return 'RET';
  if (team1Forfeit === 4 || team2Forfeit === 4) return 'WD';
  return undefined;
};

export const getGameScores = (match: PlayerLiveGameStatusData, userId: string): GameScore[] => {
  const {
    T1_P1_UserID,
    T1_P2_UserID,
    T2_P1_UserID,
    T2_P2_UserID,
    TeamID_1_Game1Score,
    TeamID_1_Game2Score,
    TeamID_1_Game3Score,
    TeamID_1_Game4Score,
    TeamID_1_Game5Score,
    TeamID_2_Game1Score,
    TeamID_2_Game2Score,
    TeamID_2_Game3Score,
    TeamID_2_Game4Score,
    TeamID_2_Game5Score,
    TeamID_1_Game1DidForfeit,
    TeamID_1_Game2DidForfeit,
    TeamID_1_Game3DidForfeit,
    TeamID_1_Game4DidForfeit,
    TeamID_1_Game5DidForfeit,
    TeamID_2_Game1DidForfeit,
    TeamID_2_Game2DidForfeit,
    TeamID_2_Game3DidForfeit,
    TeamID_2_Game4DidForfeit,
    TeamID_2_Game5DidForfeit,
  } = match;

  const isTeamOne = T1_P1_UserID === userId.toUpperCase() || T1_P2_UserID === userId.toUpperCase();
  const isTeamTwo = T2_P1_UserID === userId.toUpperCase() || T2_P2_UserID === userId.toUpperCase();

  const scores = [
    {
      team1: TeamID_1_Game1Score,
      team2: TeamID_2_Game1Score,
      forfeit1: TeamID_1_Game1DidForfeit,
      forfeit2: TeamID_2_Game1DidForfeit,
    },
    {
      team1: TeamID_1_Game2Score,
      team2: TeamID_2_Game2Score,
      forfeit1: TeamID_1_Game2DidForfeit,
      forfeit2: TeamID_2_Game2DidForfeit,
    },
    {
      team1: TeamID_1_Game3Score,
      team2: TeamID_2_Game3Score,
      forfeit1: TeamID_1_Game3DidForfeit,
      forfeit2: TeamID_2_Game3DidForfeit,
    },
    {
      team1: TeamID_1_Game4Score,
      team2: TeamID_2_Game4Score,
      forfeit1: TeamID_1_Game4DidForfeit,
      forfeit2: TeamID_2_Game4DidForfeit,
    },
    {
      team1: TeamID_1_Game5Score,
      team2: TeamID_2_Game5Score,
      forfeit1: TeamID_1_Game5DidForfeit,
      forfeit2: TeamID_2_Game5DidForfeit,
    },
  ];

  return scores.map((game) => {
    const forfeitMessage = getForfeitMessage(game.forfeit1, game.forfeit2);

    if (forfeitMessage) {
      return { userScore: '', opponentScore: '', forfeitMessage };
    }

    const isGamePlayed = game.team1 > 0 || game.team2 > 0;

    if (!isGamePlayed) {
      return { userScore: '', opponentScore: '' };
    }

    if (isTeamOne) {
      return {
        userScore: game.team1?.toString() || '0',
        opponentScore: game.team2?.toString() || '0',
        isTeamOne: true,
        isTeamTwo: false,
      };
    } else if (isTeamTwo) {
      return {
        userScore: game.team2?.toString() || '0',
        opponentScore: game.team1?.toString() || '0',
        isTeamOne: false,
        isTeamTwo: true,
      };
    }
    return { userScore: '', opponentScore: '' };
  });
};

export const getMatchStatus = (match: PlayerLiveGameStatusData): string => {
  const {
    T1_P1_UserID,
    T1_P2_UserID,
    T2_P1_UserID,
    T2_P2_UserID,
    DidCancelMatch,
    DateFinish,
    DateStart,
    DidPrintNextAvailable_DatePrinted,
    CourtTitle,
  } = match;

  if (!T1_P1_UserID && !T1_P2_UserID) {
    return 'Waiting an opponent';
  }
  if (!T2_P1_UserID && !T2_P2_UserID) {
    return 'Waiting an opponent';
  }
  if (DidCancelMatch === 'true') {
    return 'Canceled';
  }
  if (DateFinish) {
    return '';
  }
  if (DateStart) {
    return `On Court: ${CourtTitle}, ${formatDateTime(DateStart, { includeWeekday: true, type: 'datetime', localTimezone: true })}`;
  }
  if (DidPrintNextAvailable_DatePrinted && !CourtTitle) {
    return 'Waiting for a court';
  }
  return '';
};
